import React, { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Plus, Minus } from "lucide-react";
import { Icon, Reveal, EASE } from "./primitives";

function ModuleCard({ mod, accent, index }) {
  const [open, setOpen] = useState(false);
  return (
    <Reveal delay={(index % 3) * 0.08} className="h-full">
      <div
        className="group flex h-full flex-col rounded-3xl border border-black/5 bg-white p-6 shadow-[0_8px_30px_rgb(11,19,43,0.04)] transition-transform transition-shadow duration-300 ease-out hover:-translate-y-1 hover:shadow-[0_20px_50px_rgb(11,19,43,0.08)]"
        style={{ borderTop: `3px solid ${accent.color}` }}
        data-testid={`module-card-${mod.key}`}
      >
        <span
          className="flex h-12 w-12 items-center justify-center rounded-xl"
          style={{ backgroundColor: `${accent.color}12`, color: accent.color }}
        >
          <Icon name={mod.icon} size={24} />
        </span>
        <h3 className="mt-5 font-heading text-xl font-bold tracking-tight text-ajmus-ink">
          {mod.title}
        </h3>
        <p className="mt-2 flex-1 text-sm leading-relaxed text-ajmus-steel">{mod.summary}</p>

        <button
          onClick={() => setOpen((o) => !o)}
          data-testid={`module-toggle-${mod.key}`}
          aria-expanded={open}
          className="mt-5 inline-flex w-fit items-center gap-2 rounded-full px-3 py-1.5 text-xs font-semibold transition-colors duration-200"
          style={{ color: accent.color, backgroundColor: `${accent.color}0f` }}
        >
          {open ? <Minus size={14} /> : <Plus size={14} />}
          {open ? "Hide capabilities" : "View capabilities"}
        </button>

        <AnimatePresence initial={false}>
          {open && (
            <motion.ul
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: "auto", opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.3, ease: EASE }}
              className="overflow-hidden"
            >
              <div className="mt-4 grid grid-cols-1 gap-1.5 sm:grid-cols-2">
                {mod.features.map((f) => (
                  <li key={f} className="flex items-start gap-2 text-[13px] leading-snug text-ajmus-steel">
                    <span className="mt-1.5 h-1 w-1 flex-shrink-0 rounded-full" style={{ backgroundColor: accent.color }} />
                    {f}
                  </li>
                ))}
              </div>
            </motion.ul>
          )}
        </AnimatePresence>
      </div>
    </Reveal>
  );
}

export default function ModuleSection({ id, chapter, overline, title, description, modules, accent, tint }) {
  return (
    <section id={id} className="relative py-20 sm:py-28" style={{ backgroundColor: tint }} data-testid={`section-${id}`}>
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <div className="flex flex-col gap-6 lg:flex-row lg:items-end lg:justify-between">
          <Reveal>
            <div className="max-w-2xl">
              <div className="flex items-center gap-3">
                <span
                  className="font-heading text-sm font-black"
                  style={{ color: accent.color }}
                >
                  {chapter}
                </span>
                <span className="h-px w-10" style={{ backgroundColor: accent.color }} />
                <span className="font-mono text-[11px] uppercase tracking-[0.24em]" style={{ color: accent.color }}>
                  {overline}
                </span>
              </div>
              <h2 className="mt-5 font-heading text-4xl font-extrabold tracking-tight text-ajmus-ink sm:text-5xl">
                {title}
              </h2>
              <p className="mt-5 max-w-xl text-base leading-relaxed text-ajmus-steel sm:text-lg">
                {description}
              </p>
            </div>
          </Reveal>
        </div>

        <div className="mt-14 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {modules.map((mod, i) => (
            <ModuleCard key={mod.key} mod={mod} accent={accent} index={i} />
          ))}
        </div>
      </div>
    </section>
  );
}
